import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  BookOpen,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  FileCheck,
  BarChart3,
  GraduationCap,
  Shield,
  Lightbulb,
  Building2,
  ChevronRight,
  Star
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const NAACAccreditationPreparation = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'NAAC Accreditation Preparation Training',
    subtitle: 'Comprehensive Training for NAAC 2024 Assessment & Accreditation',
    description: 'Master NAAC accreditation process with latest 2024 criteria, DVV requirements, and quality indicators to achieve higher grades',
    meta_title: 'NAAC Accreditation Training 2024 | NAAC Assessment Preparation | ISDE Academy',
    meta_description: 'Professional NAAC accreditation preparation training covering 2024 revised criteria, DVV processes, SSR preparation, quality indicators. Achieve A+ grade with expert guidance.',
    meta_keywords: 'NAAC accreditation training, NAAC 2024 criteria, DVV preparation, SSR writing, NAAC assessment, higher education accreditation, quality assurance',
    canonicalUrl: 'https://isde.academy/training/naac-accreditation-preparation'
  };

  const trainingModules = [
    {
      title: 'NAAC 2024 Framework & Revised Criteria',
      icon: Award,
      duration: '4 Hours',
      topics: [
        'Understanding NAAC 2024 Assessment Framework',
        'Seven Criteria and Key Indicators',
        'Weightage Distribution and Scoring',
        'Changes from Previous Frameworks',
        'Grade Calculation and Benchmarking',
        'Understanding Peer Team Assessment'
      ],
      outcomes: [
        'Complete understanding of NAAC 2024 criteria',
        'Clarity on weightage and scoring mechanisms',
        'Readiness for grade-based assessment'
      ]
    },
    {
      title: 'Self-Study Report (SSR) Preparation',
      icon: FileCheck,
      duration: '6 Hours',
      topics: [
        'SSR Structure and Organization',
        'Writing Evidence-Based Responses',
        'Quality Indicator Documentation',
        'Data Collection and Presentation',
        'Supporting Documents Preparation',
        'Common SSR Mistakes to Avoid',
        'Best Practices for High Scoring SSR'
      ],
      outcomes: [
        'Ability to prepare comprehensive SSR',
        'Strong documentation and evidence preparation',
        'High-quality SSR submission'
      ]
    },
    {
      title: 'Data Validation & Verification (DVV)',
      icon: Shield,
      duration: '4 Hours',
      topics: [
        'DVV Process and Requirements 2024',
        'Document Verification Checklist',
        'Common DVV Clarifications and Rejections',
        'Best Practices for DVV Compliance',
        'Evidence Authenticity and Accuracy',
        'Handling DVV Queries Effectively',
        'Digital Record Management'
      ],
      outcomes: [
        'Mastery of DVV compliance',
        'Accurate and verifiable documentation',
        'Minimal DVV rejections'
      ]
    },
    {
      title: 'Quality Indicators & Metrics',
      icon: BarChart3,
      duration: '5 Hours',
      topics: [
        'Criterion 1: Curricular Aspects',
        'Criterion 2: Teaching-Learning & Evaluation',
        'Criterion 3: Research, Innovations & Extension',
        'Criterion 4: Infrastructure & Learning Resources',
        'Criterion 5: Student Support & Progression',
        'Criterion 6: Governance, Leadership & Management',
        'Criterion 7: Institutional Values & Best Practices',
        'Quantitative and Qualitative Metrics'
      ],
      outcomes: [
        'Deep understanding of all 7 criteria',
        'Ability to measure and improve quality indicators',
        'Strategic quality enhancement planning'
      ]
    },
    {
      title: 'Institutional Readiness & Gap Analysis',
      icon: Target,
      duration: '3 Hours',
      topics: [
        'Conducting Institutional Self-Assessment',
        'Identifying Strengths and Weaknesses',
        'Gap Analysis for Each Criterion',
        'Priority Setting and Action Planning',
        'Resource Allocation for Quality Enhancement',
        'Timeline Planning for NAAC Preparation'
      ],
      outcomes: [
        'Clear understanding of institutional readiness',
        'Targeted improvement strategies',
        'Effective preparation roadmap'
      ]
    },
    {
      title: 'Peer Team Visit Preparation',
      icon: Users,
      duration: '4 Hours',
      topics: [
        'Understanding Peer Team Assessment Process',
        'Campus Visit Preparation',
        'Faculty and Staff Orientation',
        'Student Interaction Preparation',
        'Facility Showcasing Best Practices',
        'Presentation and Communication Skills',
        'Handling Questions and Clarifications',
        'Post-Visit Follow-up'
      ],
      outcomes: [
        'Confident peer team interaction',
        'Well-prepared campus showcase',
        'Positive assessment outcome'
      ]
    }
  ];

  const whyChoose = [
    {
      title: 'Latest 2024 Criteria',
      description: 'Training based on most recent NAAC assessment framework and guidelines',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'DVV Expert Guidance',
      description: 'Master Data Validation & Verification to minimize rejections',
      icon: Shield,
      color: 'blue'
    },
    {
      title: 'Proven Success',
      description: 'Helped 100+ institutions achieve A and A+ grades',
      icon: Star,
      color: 'purple'
    },
    {
      title: 'Practical Approach',
      description: 'Hands-on SSR writing, documentation, and evidence preparation',
      icon: Lightbulb,
      color: 'orange'
    }
  ];

  const keyBenefits = [
    {
      title: 'For Institutions',
      icon: Building2,
      benefits: [
        'Higher NAAC grades (A or A+ achievable)',
        'Enhanced institutional credibility and reputation',
        'Improved student enrollment and retention',
        'Better funding and grant opportunities',
        'Systematic quality assurance processes',
        'Competitive advantage in higher education'
      ]
    },
    {
      title: 'For NAAC Coordinators',
      icon: GraduationCap,
      benefits: [
        'Complete understanding of NAAC process',
        'Confidence in SSR preparation and submission',
        'Expertise in DVV compliance',
        'Ability to lead institutional preparation',
        'Professional recognition and career growth',
        'Network with NAAC experts'
      ]
    }
  ];

  const deliveryOptions = [
    {
      mode: 'On-Campus Workshop',
      duration: '2-3 Days Intensive',
      description: 'Comprehensive on-site training with hands-on SSR preparation, documentation review, and peer team simulation',
      ideal: 'Best for institutions preparing for NAAC in next 6-12 months'
    },
    {
      mode: 'Online Live Training',
      duration: '6-8 Sessions (2 weeks)',
      description: 'Interactive online sessions with recorded access, SSR templates, and post-training support',
      ideal: 'Flexible option for multiple stakeholders across departments'
    },
    {
      mode: 'Hybrid Consulting',
      duration: 'Customized (1-6 months)',
      description: 'End-to-end NAAC preparation support including gap analysis, SSR preparation, mock assessment, and peer team readiness',
      ideal: 'Complete solution for first-time NAAC applicants or re-accreditation'
    }
  ];

  const targetAudience = [
    'NAAC Coordinators and Steering Committee Members',
    'Principal/Vice-Principal/Directors',
    'HODs and Department Coordinators',
    'IQAC (Internal Quality Assurance Cell) Members',
    'Faculty responsible for criterion-wise documentation',
    'Administrative staff involved in data management',
    'Quality Assurance Officers'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white py-24 mt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Award className="h-32 w-32" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">NAAC 2024 Ready</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">{pageData.title}</h1>
              <p className="text-xl md:text-2xl text-emerald-100 mb-4">{pageData.subtitle}</p>
              <p className="text-lg text-emerald-50 mb-8 max-w-3xl mx-auto">{pageData.description}</p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg font-semibold"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg font-semibold"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Training Modules */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Comprehensive Training Modules</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Structured curriculum covering all aspects of NAAC accreditation from understanding criteria to peer team visit
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {trainingModules.map((module, index) => {
              const IconComponent = module.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-emerald-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{module.title}</h3>
                          <p className="text-sm text-emerald-600 font-semibold">Duration: {module.duration}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Topics:</h4>
                        <ul className="space-y-1.5">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-emerald-50 p-3 rounded-lg">
                        <h4 className="text-xs font-semibold text-emerald-700 uppercase mb-1">Learning Outcomes:</h4>
                        <ul className="space-y-1">
                          {module.outcomes.map((outcome, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <span className="text-emerald-600">•</span>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Our NAAC Training?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: 'bg-emerald-50 text-emerald-600',
                  blue: 'bg-blue-50 text-blue-600',
                  purple: 'bg-purple-50 text-purple-600',
                  orange: 'bg-orange-50 text-orange-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full text-center hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className={`${colors[item.color]} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {keyBenefits.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-emerald-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-bold">{item.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {item.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ChevronRight className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Delivery Options */}
        <section className="bg-gradient-to-br from-gray-50 to-emerald-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Training Delivery Options</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Choose the format that best suits your institution's needs and timeline
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {deliveryOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-emerald-600">{option.mode}</h3>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{option.duration}</p>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <div className="bg-emerald-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-emerald-700 uppercase mb-1">Ideal For:</p>
                        <p className="text-sm text-gray-700">{option.ideal}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Who Should Attend?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This training is essential for all stakeholders involved in NAAC accreditation process
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-emerald-100 hover:shadow-md transition-all"
              >
                <Users className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Achieve NAAC A+ Grade?
              </h2>
              <p className="text-xl text-emerald-50 mb-8">
                Join 100+ institutions that have successfully achieved higher NAAC grades with our expert training
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg font-semibold"
                >
                  Schedule Training
                </Button>
                <Button
                  onClick={() => navigate('/training/hei-training')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-6 text-lg font-semibold"
                >
                  View All HEI Programs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default NAACAccreditationPreparation;
